import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
export function useMyRecordings() {
    return useQuery({
        queryKey: ['my-recordings'],
        queryFn: () => apiClient('/recordings/me'),
        staleTime: 1000 * 60 * 5,
    });
}
/**
 * Hover-prefetch cache. Minting the signed URL is the slowest part of opening a
 * recording (~1 API round-trip + 3 DB queries), and a hover precedes the click by
 * hundreds of milliseconds — enough to hide that latency entirely.
 *
 * One-shot and short-lived ON PURPOSE: the player re-mints URLs on a timer and on
 * self-heal, and those re-mints must always hit the network (a cached, near-stale
 * URL would re-introduce the expired-token freeze this player already fixed).
 * Note: each prefetch mints a real signed URL, so it is access-logged like a view.
 */
const STREAM_PREFETCH_TTL_MS = 15_000;
const streamPrefetchCache = new Map();
/** Warm the TLS connection to the storage origin before the video element needs it. */
const preconnected = new Set();
function preconnectTo(url) {
    try {
        const origin = new URL(url).origin;
        if (preconnected.has(origin))
            return;
        preconnected.add(origin);
        // No crossOrigin: the <video> fetches in credentialed no-cors mode, and the
        // browser only reuses warmed sockets whose credentials mode matches.
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = origin;
        document.head.appendChild(link);
    }
    catch {
        /* malformed URL — nothing to warm */
    }
}
/** Fire-and-forget: mint the stream URL ahead of the click. Call this on INTENT
 *  (pointerdown / sustained hover), not on mere proximity: every mint is a real,
 *  audit-logged access and counts against the rate limit. */
export function prefetchRecordingStream(recordingId) {
    const cached = streamPrefetchCache.get(recordingId);
    if (cached && Date.now() - cached.at < STREAM_PREFETCH_TTL_MS)
        return;
    const promise = apiClient(`/recordings/${recordingId}/stream`);
    promise
        .then((stream) => preconnectTo(stream.url))
        .catch(() => streamPrefetchCache.delete(recordingId));
    const entry = { promise, at: Date.now() };
    streamPrefetchCache.set(recordingId, entry);
    // Evict unconsumed entries so signed-URL promises don't outlive their usefulness.
    setTimeout(() => {
        if (streamPrefetchCache.get(recordingId) === entry)
            streamPrefetchCache.delete(recordingId);
    }, STREAM_PREFETCH_TTL_MS + 1_000);
}
/** Imperative: fetch a short-lived signed stream URL for one recording. */
export function fetchRecordingStream(recordingId) {
    const cached = streamPrefetchCache.get(recordingId);
    if (cached) {
        // Consume exactly once: subsequent calls (token-refresh re-mints) go to the network.
        streamPrefetchCache.delete(recordingId);
        if (Date.now() - cached.at < STREAM_PREFETCH_TTL_MS)
            return cached.promise;
    }
    return apiClient(`/recordings/${recordingId}/stream`);
}
//# sourceMappingURL=useMyRecordings.js.map